import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, PhoneOff, Mic, Bot, User, Loader2, AlertTriangle, Send, Music, ChevronDown, Search } from 'lucide-react';
import { GlassCard } from '../components/ui/GlassCard';
import { AgentAvatar } from '../components/voice/AgentAvatar';
import { VoiceOrb } from '../components/voice/VoiceOrb';
import { AgentCommsPanel, CommEvent } from '../components/voice/AgentCommsPanel';
import { AGENTS, AgentKey, agentKeyFromType, loadVoices, voiceForAgent } from '../lib/agents';
import { startHoldMusic, stopHoldMusic } from '../lib/holdMusic';
import { CallAudio, blobToBase64 } from '../lib/callAudio';
import { pageTransition } from '../hooks/useAnimation';

type Turn = { speaker: 'caller' | 'agent'; agent?: AgentKey; text: string; tokens?: number };
type Phase = 'idle' | 'awaiting_consent' | 'awaiting_details' | 'verifying' | 'verified';

// Explicit hang-up phrases — end the call at any point when spoken by the caller.
const HANGUP_RE = /\b((end|cut|stop|drop|hang up|finish|terminate) (the |this )?call|cut me off|hang ?up|good ?bye|bye bye|cheers,? bye|disconnect|end now)\b|^\s*bye[\s.!]*$/i;
// Conversational "we're done" phrases — only end once the caller is verified and
// in conversation (so e.g. "no thank you" during consent still hits the decline flow).
const CLOSING_RE = /\b(that'?s (all|it|everything)|that (will|'?ll) be all|nothing (more|else)|no(thing)? else|i'?m done|i am done|all (good|done)|no thank(s| you)|that is (all|it|everything)|thats it)\b|^\s*(no|nope)[\s.!]*$/i;

// Agents that speak (each can be assigned a different human voice).
const VOICE_AGENTS: AgentKey[] = ['voice', 'auth', 'network', 'ticket', 'supervisor'];
const DEFAULT_AGENT_VOICES: Record<string, string> = {
  voice: 'alba', auth: 'alan', network: 'ryan', ticket: 'cori', supervisor: 'joe',
};
type OrbState = 'idle' | 'listening' | 'thinking' | 'speaking';

interface Customer { phone_number: string; name: string; account_number: string; city: string; plan: string; account_status: string; }

const SR: any = (typeof window !== 'undefined' && ((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition)) || null;
const API = '/api/agents';

const AGENT_LINES: Partial<Record<AgentKey, string>> = {
  auth: 'Verifying identity.',
  network: 'Running network diagnostics.',
  ticket: 'Checking your account and tickets.',
  supervisor: 'Reviewing the outcome.',
};

// "Please hold" lines the agent says before going to check the systems; hold music
// then plays during the background lookup so the line is never silent.
const HOLD_LINES = [
  'Sure, let me check that for you. One moment please.',
  'Let me look into that for you — please bear with me a moment.',
  "Okay, I'll check our system now. Please hold for a moment.",
  'Let me pull up those details for you. One moment, please.',
];
const pickHold = () => HOLD_LINES[Math.floor(Math.random() * HOLD_LINES.length)];

export function VoiceCall() {
  const hasTTS = typeof window !== 'undefined' && 'speechSynthesis' in window;
  const hasSTT = !!SR;

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selected, setSelected] = useState<Customer | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');
  const pickerRef = useRef<HTMLDivElement>(null);

  const [inCall, setInCall] = useState(false);
  const [phase, setPhase] = useState<Phase>('idle');
  const [orb, setOrb] = useState<OrbState>('idle');
  const [speakingAgent, setSpeakingAgent] = useState<AgentKey | null>(null);
  const [musicOn, setMusicOn] = useState(false);
  const [recording, setRecording] = useState(false);
  const [partial, setPartial] = useState('');
  const [turns, setTurns] = useState<Turn[]>([]);
  const [comms, setComms] = useState<CommEvent[]>([]);
  const [insight, setInsight] = useState<any | null>(null);
  const [error, setError] = useState('');
  const [typed, setTyped] = useState('');
  const [voiceCatalog, setVoiceCatalog] = useState<{ id: string; label: string; gender: string; accent: string }[]>([]);
  const [agentVoices, setAgentVoices] = useState<Record<string, string>>(() => {
    try { return { ...DEFAULT_AGENT_VOICES, ...JSON.parse(localStorage.getItem('agentVoices') || '{}') }; }
    catch { return { ...DEFAULT_AGENT_VOICES }; }
  });

  const recogRef = useRef<any>(null);
  const agentVoicesRef = useRef<Record<string, string>>({ ...DEFAULT_AGENT_VOICES });
  const micStreamRef = useRef<MediaStream | null>(null);
  const callAudioRef = useRef<CallAudio | null>(null);
  const callIdRef = useRef('');
  const phaseRef = useRef<Phase>('idle');
  const inCallRef = useRef(false);
  const busyRef = useRef(false);
  const commsTimer = useRef<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const setPhaseBoth = (p: Phase) => { phaseRef.current = p; setPhase(p); };

  useEffect(() => { loadVoices(); fetchCustomers(); fetchVoiceCatalog(); return () => endCall(); }, []);

  useEffect(() => { agentVoicesRef.current = agentVoices; }, [agentVoices]);

  const fetchVoiceCatalog = async () => {
    try {
      const r = await fetch(`${API}/voices`);
      const d = await r.json();
      setVoiceCatalog(d.voices || []);
    } catch { /* catalog optional */ }
  };

  const pickVoice = (agent: AgentKey, id: string) => {
    setAgentVoices((prev) => {
      const next = { ...prev, [agent]: id };
      agentVoicesRef.current = next;
      try { localStorage.setItem('agentVoices', JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const previewVoice = async (agent: AgentKey, voiceId: string) => {
    try {
      const r = await fetch(`${API}/tts`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: `Hi, I'm ${AGENTS[agent].name}. This is how I sound.`, agent, voice: voiceId }),
      });
      if (r.ok) await playStandalone(await r.arrayBuffer());
    } catch { /* ignore */ }
  };

  const playStandalone = async (data: ArrayBuffer): Promise<void> => {
    try {
      const AC = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!AC) return;
      const ctx: AudioContext = new AC();
      const buf = await ctx.decodeAudioData(data.slice(0));
      const src = ctx.createBufferSource();
      src.buffer = buf; src.connect(ctx.destination);
      await new Promise<void>((res) => { src.onended = () => { try { ctx.close(); } catch {} res(); }; src.start(); });
    } catch { /* ignore */ }
  };
  useEffect(() => {
    if (!pickerOpen) return;
    const onDoc = (e: MouseEvent) => { if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) setPickerOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [pickerOpen]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' }); }, [turns, partial, orb]);

  const fetchCustomers = async () => {
    try {
      const r = await fetch(`${API}/customers`);
      const d = await r.json();
      setCustomers(d.customers || []);
      if (d.customers?.length) setSelected(d.customers[0]);
    } catch { setError('Could not load customer list'); }
  };

  // ── Speech ────────────────────────────────────────────────────────────────
  // Browser TTS fallback (used only if server TTS fails).
  const speakBrowser = (agent: AgentKey, text: string, finish: () => void) => {
    if (!hasTTS || !text) { finish(); return; }
    try {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      const v = voiceForAgent(agent);
      if (v) u.voice = v;
      u.lang = 'en-GB'; u.pitch = AGENTS[agent].pitch; u.rate = AGENTS[agent].rate;
      u.onend = finish; u.onerror = finish;
      window.speechSynthesis.speak(u);
      setTimeout(finish, Math.min(20000, 1500 + text.length * 70));
    } catch { finish(); }
  };

  // Speak via the chosen natural Piper voice (played live + captured into the
  // recording). Falls back to the browser voice if server TTS is unavailable.
  const speakAs = (agent: AgentKey, text: string, after: () => void = () => {}) => {
    let done = false;
    const finish = () => { if (done) return; done = true; setOrb('idle'); setSpeakingAgent(null); after(); };
    if (!text) { finish(); return; }
    setOrb('speaking'); setSpeakingAgent(agent);
    (async () => {
      try {
        const voiceId = agentVoicesRef.current[agent] || agentVoicesRef.current.voice || 'alba';
        const r = await fetch(`${API}/tts`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text, agent, voice: voiceId }),
        });
        if (!r.ok) throw new Error('tts');
        const buf = await r.arrayBuffer();
        const ca = callAudioRef.current;
        if (ca) await ca.play(buf); else await playStandalone(buf);
        finish();
      } catch {
        speakBrowser(agent, text, finish);
      }
    })();
  };

  const speakSequence = (items: { agent: AgentKey; text: string }[], after: () => void = () => {}) => {
    if (!items.length) { after(); return; }
    const [head, ...rest] = items;
    speakAs(head.agent, head.text, () => speakSequence(rest, after));
  };

  // ── Comms animation ─────────────────────────────────────────────────────────
  const runComms = (events: CommEvent[]) => {
    if (commsTimer.current) { clearInterval(commsTimer.current); commsTimer.current = null; }
    if (!events.length) return;
    let i = 0;
    setComms([events[0]]);
    i = 1;
    commsTimer.current = window.setInterval(() => {
      if (i >= events.length) { if (commsTimer.current) clearInterval(commsTimer.current); commsTimer.current = null; return; }
      setComms((c) => [...c, events[i]]);
      i++;
    }, 950);
  };

  // ── Mic lifecycle ───────────────────────────────────────────────────────────
  const ensureMic = async (): Promise<boolean> => {
    if (!navigator.mediaDevices?.getUserMedia) return true;
    try {
      if (!micStreamRef.current || !micStreamRef.current.active) {
        // Disable echo cancellation / noise suppression so the agent's spoken
        // replies (played on the speaker) are also captured into the recording.
        micStreamRef.current = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false } as any,
        });
      }
      return true;
    } catch { setError('Microphone permission denied. Allow mic access and press “Start call”.'); return false; }
  };
  const releaseMic = () => {
    try { micStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
    micStreamRef.current = null;
  };

  const startListening = () => {
    if (!hasSTT || !inCallRef.current || busyRef.current) return;
    try { recogRef.current?.abort?.(); } catch {}
    try {
      const recog = new SR();
      recog.lang = 'en-GB'; recog.interimResults = true; recog.continuous = false; recog.maxAlternatives = 1;
      recogRef.current = recog;
      recog.onstart = () => { setOrb('listening'); setError(''); };
      recog.onresult = (ev: any) => {
        let final = '', interim = '';
        for (let i = ev.resultIndex; i < ev.results.length; i++) {
          const r = ev.results[i];
          if (r.isFinal) final += r[0].transcript; else interim += r[0].transcript;
        }
        setPartial(interim);
        if (final.trim()) { setPartial(''); setOrb('idle'); try { recog.stop(); } catch {} handleUtterance(final.trim()); }
      };
      recog.onerror = (ev: any) => {
        setOrb('idle');
        if (ev.error === 'not-allowed' || ev.error === 'service-not-allowed') setError('Microphone permission denied.');
        else if (ev.error !== 'no-speech' && ev.error !== 'aborted') setError(`Speech error: ${ev.error}`);
      };
      recog.onend = () => { if (orb === 'listening') setOrb('idle'); };
      recog.start();
    } catch (e: any) { setError(e?.message || 'Could not start microphone'); }
  };

  // ── Call flow ───────────────────────────────────────────────────────────────
  const addTurn = (t: Turn) => setTurns((prev) => [...prev, t]);

  const startCall = async () => {
    if (!selected) { setError('Choose a number to call from first.'); return; }
    setError('');
    if (hasSTT && !(await ensureMic())) return;
    // Audio engine for live agent voice (Piper) + recording mix.
    callAudioRef.current = new CallAudio();
    callAudioRef.current.ensureCtx();
    setTurns([]); setComms([]);
    callIdRef.current = `WEBCALL-${Date.now()}`;
    inCallRef.current = true; setInCall(true);
    busyRef.current = true;
    try {
      const r = await fetch(`${API}/call/start`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ call_id: callIdRef.current, from_number: selected.phone_number }),
      });
      const d = await r.json();
      // First step: ask for recording consent (recording starts once granted).
      setPhaseBoth('awaiting_consent');
      addTurn({ speaker: 'agent', agent: 'voice', text: d.reply });
      speakAs('voice', d.reply, () => { busyRef.current = false; startListening(); });
    } catch { setError('Failed to start call'); busyRef.current = false; }
  };

  const startRecording = async () => {
    if (!micStreamRef.current) return;
    try {
      if (!callAudioRef.current) callAudioRef.current = new CallAudio();
      await callAudioRef.current.startRecording(micStreamRef.current);
      setRecording(true);
    } catch { setRecording(false); }
  };

  const goodbyeAndEnd = () => {
    busyRef.current = true;
    speakAs('voice', 'Thank you for calling Vokentra. Goodbye!', () => endCall());
  };

  const handleUtterance = async (text: string) => {
    if (!inCallRef.current) return;
    addTurn({ speaker: 'caller', text });

    // Caller asked to end the call → say goodbye and hang up.
    // Explicit hang-ups end any time; soft "we're done" phrases only once verified.
    const verified = phaseRef.current === 'verified';
    if (HANGUP_RE.test(text) || (verified && CLOSING_RE.test(text))) {
      goodbyeAndEnd();
      return;
    }

    busyRef.current = true;
    setOrb('thinking');

    try {
      if (phaseRef.current === 'awaiting_consent') {
        const r = await fetch(`${API}/call/turn`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ call_id: callIdRef.current, message: text }),
        });
        const d = await r.json();
        // Caller declined recording → agent explains and ends the call.
        if (d.phase === 'declined' || d.end) {
          addTurn({ speaker: 'agent', agent: (d.agent || 'supervisor') as AgentKey, text: d.reply });
          speakAs((d.agent || 'supervisor') as AgentKey, d.reply, () => endCall());
          return;
        }
        if (d.record) { await startRecording(); }
        setPhaseBoth('awaiting_details');
        addTurn({ speaker: 'agent', agent: 'voice', text: d.reply });
        speakAs('voice', d.reply, () => { busyRef.current = false; startListening(); });
        return;
      }

      if (phaseRef.current === 'awaiting_details') {
        const r = await fetch(`${API}/call/turn`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ call_id: callIdRef.current, message: text }),
        });
        const d = await r.json();
        setPhaseBoth('verifying');
        addTurn({ speaker: 'agent', agent: 'voice', text: d.reply });
        if (d.comms) runComms(d.comms.map((c: any, i: number) => ({ id: `v${i}-${Date.now()}`, from: c.from, to: c.to, label: c.label })));
        // Speak the "please hold" line first, THEN start hold music while the
        // account check runs in the background (no overlap, no silence).
        speakAs('voice', d.reply, async () => {
          if (d.music) { setMusicOn(true); startHoldMusic(); setOrb('thinking'); }
          await verifyStep();
        });
        return;
      }

      // verified → real query: acknowledge, then play hold music while the agent
      // checks the database + reasons in the background (so the line is never silent).
      setOrb('thinking');
      runComms([{ id: `t${Date.now()}`, from: 'voice', to: 'auth', label: 'routing' }]);

      const holdLine = pickHold();
      addTurn({ speaker: 'agent', agent: 'voice', text: holdLine });

      // Kick off the backend work in parallel with the spoken "please hold" line.
      let workDone = false;
      const workP = fetch(`${API}/call/turn`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ call_id: callIdRef.current, message: text }),
      }).then((resp) => resp.json()).then((j) => { workDone = true; return j; });

      // Speak the hold line; if the lookup is still running when it ends, play music.
      await new Promise<void>((resolve) => speakAs('voice', holdLine, resolve));
      if (!workDone) { setMusicOn(true); startHoldMusic(); setOrb('thinking'); }

      const d = await workP;
      stopHoldMusic(); setMusicOn(false);
      const di = d?.intent || {};
      setInsight({
        intent: di.title || di.primary_intent || '—',
        understanding: d?.understanding || '',
        route: d?.route || di.route || '—',
        specialist: d?.specialist || '—',
        sentiment: typeof di.sentiment === 'number' ? di.sentiment : null,
        urgency: di.urgency || '—',
        checks: d?.checks_performed || [],
        resolution: d?.trace?.agent_results?.[`${d?.specialist}_1`]?.output?.resolution || di.title || '',
        ticket: d?.ticket_id || null,
        escalation: !!d?.escalation,
        nextAction: d?.final_action || '—',
        tokens: d?.token_usage?.total_tokens || 0,
      });
      const ar = d?.trace?.agent_results || {};
      const ev: CommEvent[] = [];
      Object.entries(ar).forEach(([k, v]: any, idx) => {
        const key = agentKeyFromType(k);
        ev.push({ id: `c${idx}-${Date.now()}`, from: 'voice', to: key, label: 'delegate' });
        (v.tool_calls || []).forEach((tc: any, ti: number) =>
          ev.push({ id: `m${idx}-${ti}-${Date.now()}`, from: key, to: 'mcp', label: tc.tool_name }));
      });
      if (ev.length) runComms(ev);
      // Agent returns from the hold and gives the grounded answer in its own voice.
      const replyAgent: AgentKey = (d?.specialist && (AGENTS as any)[d.specialist]) ? d.specialist : 'voice';
      addTurn({ speaker: 'agent', agent: replyAgent, text: d.reply, tokens: d?.token_usage?.total_tokens });
      speakAs(replyAgent, d.reply, () => { busyRef.current = false; startListening(); });
    } catch {
      setError('Agent unavailable'); busyRef.current = false; setOrb('idle');
    }
  };

  const verifyStep = async () => {
    try {
      const r = await fetch(`${API}/call/verify`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ call_id: callIdRef.current }),
      });
      const d = await r.json();
      stopHoldMusic(); setMusicOn(false);
      addTurn({ speaker: 'agent', agent: d.agent || 'auth', text: d.reply });
      if (d.phase === 'verified') {
        setPhaseBoth('verified');
        speakAs(d.agent || 'auth', d.reply, () => { busyRef.current = false; startListening(); });
      } else if (d.phase === 'retry') {
        // Details didn't match — loop back and let the caller repeat them.
        setPhaseBoth('awaiting_details');
        speakAs(d.agent || 'auth', d.reply, () => { busyRef.current = false; startListening(); });
      } else if (d.end) {
        // Out of attempts — agent gives the human support number and ends the call.
        speakAs((d.agent || 'supervisor') as AgentKey, d.reply, () => endCall());
      } else {
        speakAs(d.agent || 'supervisor', d.reply, () => { busyRef.current = false; });
      }
    } catch { stopHoldMusic(); setMusicOn(false); setError('Verification failed'); busyRef.current = false; }
  };

  const endCall = () => {
    const callId = callIdRef.current;
    const wasInCall = inCallRef.current;
    inCallRef.current = false; busyRef.current = false;
    setInCall(false); setPhaseBoth('idle'); setOrb('idle'); setSpeakingAgent(null); setPartial('');
    setComms([]); setMusicOn(false); setRecording(false); setInsight(null);
    if (commsTimer.current) { clearInterval(commsTimer.current); commsTimer.current = null; }
    try { recogRef.current?.abort?.(); } catch {}
    try { window.speechSynthesis.cancel(); } catch {}
    stopHoldMusic();
    // Stop the recorder and grab the mixed audio (customer + agent) before releasing the mic.
    let wav: Blob | null = null;
    try { wav = callAudioRef.current?.stop() ?? null; } catch { wav = null; }
    callAudioRef.current = null;
    releaseMic(); // mic indicator turns off

    // Upload the recording, then finalize the transcript (ordered, best-effort).
    if (wasInCall && callId) {
      (async () => {
        try {
          if (wav && wav.size > 44) {
            const b64 = await blobToBase64(wav);
            await fetch(`${API}/call/recording`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ call_id: callId, audio_b64: b64, format: 'wav' }),
            });
          }
        } catch { /* ignore */ }
        try {
          await fetch(`${API}/call/end`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ call_id: callId }),
          });
        } catch { /* ignore */ }
      })();
    }
  };

  const sendTyped = () => {
    if (!typed.trim() || busyRef.current || !inCallRef.current) return;
    const m = typed.trim(); setTyped(''); handleUtterance(m);
  };

  const orbColor = speakingAgent ? AGENTS[speakingAgent].color : '#e60000';

  const pickerResults = customers.filter((c) => {
    const q = pickerQuery.trim().toLowerCase();
    if (!q) return true;
    return c.name.toLowerCase().includes(q) || c.phone_number.includes(q) ||
      (c.account_number || '').toLowerCase().includes(q) || (c.city || '').toLowerCase().includes(q);
  });

  return (
    <motion.div variants={pageTransition} initial="initial" animate="animate" exit="exit" className="p-6">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-text-primary flex items-center gap-2">
          <Phone className="w-6 h-6 text-vokentra" /> Voice Call
        </h1>
        <p className="text-sm text-text-muted mt-1">Pick a caller, verify identity, then talk to the multi-agent AI</p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* ── Left: call + conversation ───────────────────────────────── */}
        <div className="xl:col-span-2 space-y-4">
          {!hasSTT && (
            <GlassCard padding="md">
              <div className="flex items-start gap-3 text-warning text-sm">
                <AlertTriangle className="w-5 h-5 mt-0.5" />
                <p>Voice needs Chrome or Edge. You can still use the text box below.</p>
              </div>
            </GlassCard>
          )}

          {/* Number picker (plain container so the dropdown isn't clipped) */}
          <div ref={pickerRef} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4">
            <label className="text-[11px] text-text-muted">Calling from ({customers.length || 100} synthetic customers)</label>
            <div className="relative mt-1">
              <button disabled={inCall} onClick={() => { setPickerOpen((o) => !o); setPickerQuery(''); }}
                className="w-full flex items-center justify-between gap-2 bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2.5 text-sm text-text-primary hover:border-white/20 disabled:opacity-50 transition-colors">
                <span className="truncate">{selected ? `${selected.name} · ${selected.phone_number}` : 'Select a number…'}</span>
                <ChevronDown className={`w-4 h-4 text-text-muted flex-shrink-0 transition-transform ${pickerOpen ? 'rotate-180' : ''}`} />
              </button>

              {pickerOpen && !inCall && (
                <div className="absolute left-0 right-0 top-full z-50 mt-1 rounded-xl border border-white/[0.12] bg-[#0c0d12] shadow-2xl overflow-hidden">
                  <div className="p-2 border-b border-white/[0.08]">
                    <div className="flex items-center gap-2 bg-white/[0.04] rounded-lg px-2.5 py-1.5">
                      <Search className="w-4 h-4 text-text-muted flex-shrink-0" />
                      <input autoFocus value={pickerQuery} onChange={(e) => setPickerQuery(e.target.value)}
                        placeholder="Search name, number, account, city…"
                        className="bg-transparent text-sm text-text-primary placeholder:text-text-muted outline-none w-full" />
                    </div>
                  </div>
                  <div className="max-h-72 overflow-y-auto overscroll-contain">
                    {pickerResults.length === 0 && (
                      <p className="text-sm text-text-muted text-center py-6">No matching customers</p>
                    )}
                    {pickerResults.map((c) => (
                      <button key={c.phone_number} onClick={() => { setSelected(c); setPickerOpen(false); }}
                        className={`w-full text-left px-3 py-2 text-sm border-l-2 transition-colors ${selected?.phone_number === c.phone_number ? 'border-vokentra bg-vokentra/10' : 'border-transparent hover:bg-white/[0.05]'}`}>
                        <div className="text-text-primary truncate">{c.name} <span className="text-text-muted">· {c.phone_number}</span></div>
                        <div className="text-[11px] text-text-muted truncate">{c.account_number} · {c.city} · {c.plan}</div>
                      </button>
                    ))}
                  </div>
                  <div className="px-3 py-1.5 border-t border-white/[0.08] text-[10px] text-text-muted">
                    {pickerResults.length} of {customers.length} shown
                  </div>
                </div>
              )}
            </div>
            {selected && (
              <p className="text-[11px] text-text-muted mt-2">
                When asked, say your name <b className="text-text-secondary">{selected.name}</b> and account
                <b className="text-text-secondary"> {selected.account_number}</b> to verify.
              </p>
            )}
          </div>

          {/* Per-agent voice assignment (natural human voices) */}
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-[11px] text-text-muted">Agent voices — assign a different human voice to each agent</label>
              <span className="text-[10px] text-text-muted">{voiceCatalog.length || 8} voices</span>
            </div>
            <div className="space-y-2">
              {VOICE_AGENTS.map((agent) => (
                <div key={agent} className="flex items-center gap-2">
                  <AgentAvatar agent={agent} size={26} />
                  <span className="text-xs text-text-secondary w-28 truncate">{AGENTS[agent].name}</span>
                  <select
                    value={agentVoices[agent] || 'alba'}
                    onChange={(e) => pickVoice(agent, e.target.value)}
                    className="flex-1 min-w-[160px] bg-white/[0.04] border border-white/[0.08] rounded-lg px-2 py-1.5 text-xs text-text-primary outline-none">
                    {voiceCatalog.length === 0 && <option value="alba">Alba — British, female</option>}
                    {voiceCatalog.map((v) => (
                      <option key={v.id} value={v.id}>{v.label} — {v.accent}, {v.gender}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => previewVoice(agent, agentVoices[agent] || 'alba')}
                    title="Preview voice"
                    className="px-2 py-1.5 rounded-lg bg-white/[0.05] border border-white/[0.1] text-xs text-text-primary hover:bg-white/[0.08]">
                    ▶
                  </button>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-text-muted mt-2">Each agent speaks in its own voice — live and in the recording.</p>
          </div>

          {/* Orb + controls */}
          <GlassCard padding="md">
            <div className="flex flex-col items-center gap-3">
              <VoiceOrb state={orb} color={orbColor}
                label={orb === 'idle' && inCall ? 'On call' : !inCall ? 'Ready' : undefined} />
              <div className="h-5 text-sm flex items-center gap-2">
                {speakingAgent && orb === 'speaking' && (
                  <span style={{ color: AGENTS[speakingAgent].color }} className="flex items-center gap-1.5">
                    <AgentAvatar agent={speakingAgent} size={18} /> {AGENTS[speakingAgent].name} speaking…
                  </span>
                )}
                {orb === 'listening' && <span className="text-success flex items-center gap-1.5"><Mic className="w-4 h-4 animate-pulse" /> Listening…</span>}
                {orb === 'thinking' && <span className="text-info flex items-center gap-1.5"><Loader2 className="w-4 h-4 animate-spin" /> Working…</span>}
                {musicOn && <span className="text-pink-400 flex items-center gap-1.5"><Music className="w-4 h-4 animate-pulse" /> Hold music</span>}
                {recording && <span className="text-error flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-error animate-pulse" /> Recording</span>}
              </div>

              <div className="flex items-center gap-3">
                {!inCall ? (
                  <button onClick={startCall} disabled={!selected}
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-success text-white font-medium hover:opacity-90 disabled:opacity-40">
                    <Phone className="w-5 h-5" /> Start call
                  </button>
                ) : (
                  <>
                    <button onClick={endCall} className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-vokentra text-white font-medium hover:bg-vokentra-dark">
                      <PhoneOff className="w-5 h-5" /> End call
                    </button>
                    {hasSTT && (
                      <button onClick={startListening} disabled={busyRef.current || orb === 'listening'}
                        className="inline-flex items-center gap-2 px-4 py-3 rounded-xl bg-white/[0.05] border border-white/[0.1] text-text-primary hover:bg-white/[0.08] disabled:opacity-40">
                        <Mic className={`w-5 h-5 ${orb === 'listening' ? 'text-success animate-pulse' : ''}`} /> Speak
                      </button>
                    )}
                  </>
                )}
              </div>
              {phase !== 'idle' && (
                <span className="text-[11px] text-text-muted">
                  Status: {phase === 'awaiting_consent' ? 'Asking recording consent' : phase === 'awaiting_details' ? 'Awaiting name + account number' : phase === 'verifying' ? 'Verifying…' : 'Verified'}
                </span>
              )}
            </div>
          </GlassCard>

          {error && <p className="text-sm text-error">{error}</p>}

          {/* Conversation */}
          <GlassCard padding="md">
            <div ref={scrollRef} className="space-y-3 max-h-[36vh] overflow-y-auto">
              {turns.length === 0 && <p className="text-sm text-text-muted text-center py-6">Pick a number and press “Start call”.</p>}
              {turns.map((t, i) => (
                <div key={i} className={`rounded-xl p-3 ${t.speaker === 'caller' ? 'bg-white/[0.04]' : 'bg-vokentra/10'}`}>
                  <div className="flex items-center gap-1.5 text-[11px] text-text-muted mb-1">
                    {t.speaker === 'caller' ? <User className="w-3.5 h-3.5" /> : <AgentAvatar agent={t.agent || 'voice'} size={16} />}
                    <span className="font-medium">{t.speaker === 'caller' ? 'You' : AGENTS[t.agent || 'voice'].name}</span>
                    {t.tokens !== undefined && <span className="ml-1 text-info">· {t.tokens} tokens</span>}
                  </div>
                  <p className="text-sm text-text-primary leading-relaxed">{t.text}</p>
                </div>
              ))}
              {partial && (
                <div className="rounded-xl p-3 bg-white/[0.02] border border-dashed border-white/10">
                  <div className="flex items-center gap-1.5 text-[11px] text-text-muted mb-1"><Mic className="w-3.5 h-3.5" /> You (speaking)</div>
                  <p className="text-sm text-text-secondary italic">{partial}</p>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/[0.06]">
              <input value={typed} onChange={(e) => setTyped(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') sendTyped(); }}
                placeholder={inCall ? 'Type instead of speaking…' : 'Start a call to chat'} disabled={!inCall || busyRef.current}
                className="flex-1 bg-white/[0.03] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-text-primary placeholder:text-text-muted outline-none disabled:opacity-50" />
              <button onClick={sendTyped} disabled={!typed.trim() || !inCall}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-vokentra text-white text-sm hover:bg-vokentra-dark disabled:opacity-40">
                <Send className="w-4 h-4" /> Send
              </button>
            </div>
          </GlassCard>
        </div>

        {/* ── Right: agent reference (top) + comms (bottom) ───────────── */}
        <div className="xl:col-span-1 space-y-6">
          <div className="rounded-2xl border border-white/[0.06] bg-background/60 backdrop-blur-xl p-4">
            <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-3">Agents on this call</h3>
            <div className="space-y-2">
              {(['voice', 'auth', 'network', 'ticket', 'supervisor', 'music', 'mcp'] as AgentKey[]).map((k) => (
                <div key={k} className={`flex items-center gap-3 rounded-xl p-2 transition-colors ${speakingAgent === k ? 'bg-white/[0.05]' : ''}`}>
                  <AgentAvatar agent={k} size={36} active={speakingAgent === k || (k === 'music' && musicOn)} />
                  <div className="min-w-0">
                    <p className="text-sm text-text-primary truncate">{AGENTS[k].name}</p>
                    <p className="text-[11px] text-text-muted truncate">{AGENTS[k].role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {insight && (
            <div className="rounded-2xl border border-white/[0.06] bg-background/60 backdrop-blur-xl p-4">
              <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-3">Live AI Insight</h3>
              <div className="space-y-2.5 text-sm">
                <div className="flex justify-between gap-3">
                  <span className="text-text-muted">Detected intent</span>
                  <span className="text-text-primary text-right truncate">{insight.intent}</span>
                </div>
                {insight.understanding && (
                  <div>
                    <div className="text-text-muted mb-1">Agent understood</div>
                    <div className="text-[12px] text-text-secondary leading-snug italic">"{insight.understanding}"</div>
                  </div>
                )}
                <div className="flex justify-between gap-3">
                  <span className="text-text-muted">Selected agent</span>
                  <span className="text-text-primary text-right capitalize">{insight.specialist} · {insight.route}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-text-muted">Sentiment</span>
                  <span className={`text-right ${insight.sentiment != null && insight.sentiment < -0.2 ? 'text-danger' : insight.sentiment != null && insight.sentiment > 0.2 ? 'text-success' : 'text-text-primary'}`}>
                    {insight.sentiment != null ? insight.sentiment.toFixed(2) : '—'} · {insight.urgency}
                  </span>
                </div>
                <div>
                  <div className="text-text-muted mb-1">Backend checks</div>
                  <div className="flex flex-wrap gap-1.5">
                    {insight.checks.length ? insight.checks.map((c: string) => (
                      <span key={c} className="px-2 py-0.5 rounded-md bg-white/[0.06] text-[11px] text-text-secondary">{c}</span>
                    )) : <span className="text-[11px] text-text-muted">none</span>}
                  </div>
                </div>
                {insight.resolution && (
                  <div>
                    <div className="text-text-muted mb-1">Recommended solution</div>
                    <div className="text-[12px] text-text-secondary leading-snug">{insight.resolution}</div>
                  </div>
                )}
                <div className="flex justify-between gap-3 pt-2 border-t border-white/[0.06]">
                  <span className="text-text-muted">Ticket</span>
                  <span className="text-text-primary">{insight.ticket || '—'}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-text-muted">Escalation</span>
                  <span className={insight.escalation ? 'text-danger font-medium' : 'text-success'}>
                    {insight.escalation ? 'Escalated to human' : 'Resolved by AI'}
                  </span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-text-muted">Next action</span>
                  <span className="text-text-primary text-right truncate">{insight.nextAction}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-text-muted">LLM tokens</span>
                  <span className="text-text-primary">{insight.tokens}</span>
                </div>
              </div>
            </div>
          )}

          <AgentCommsPanel events={comms} playing={orb === 'thinking' || musicOn} />
        </div>
      </div>

      <p className="text-[11px] text-text-muted mt-4">
        Demo: choose any of the 100 numbers, then provide that customer’s name + account number to be verified
        against the database. Hold music plays during the check. Try “my broadband is slow” or “I was overcharged”.
      </p>
    </motion.div>
  );
}

export default VoiceCall;
